import { type DismissalData, type UndoDismissalData } from '../../../graphql-result';
import {
	type AppRecMutationResponse,
	type DismissRecommendationResponse,
	type MutationResponseBroker,
	type UndoDismissalResponse,
} from '../../../response-broker';
import { DismissalSuccessResultCreator, UndoDismissalSuccessResultCreator } from '../index';

export class DismissRecommendationRequestBroker
	implements
		MutationResponseBroker<
			DismissRecommendationResponse,
			DismissRecommendationResponse['dismissRecommendation'],
			DismissalData
		>
{
	extract(
		response: AppRecMutationResponse | undefined,
	): DismissRecommendationResponse['dismissRecommendation'] | null {
		return this.isDismissRecommendationResponse(response) ? response.dismissRecommendation : null;
	}

	getSuccessResultCreator(
		response: Response,
		mutationResponse: DismissRecommendationResponse['dismissRecommendation'],
	): DismissalSuccessResultCreator<DismissalData> {
		return new DismissalSuccessResultCreator(response, this.getDismissalData(mutationResponse));
	}

	private getDismissalData({
		dismissal,
	}: DismissRecommendationResponse['dismissRecommendation']): NonNullable<
		DismissRecommendationResponse['dismissRecommendation']['dismissal']
	> {
		if (!dismissal) {
			throw new Error('Dismissal data is missing');
		}

		return dismissal;
	}

	private isDismissRecommendationResponse(
		response: AppRecMutationResponse | undefined,
	): response is DismissRecommendationResponse {
		return response?.hasOwnProperty('dismissRecommendation') ?? false;
	}
}

export class UndoDismissalRequestBroker
	implements
		MutationResponseBroker<
			UndoDismissalResponse,
			UndoDismissalResponse['undoDismissal'],
			UndoDismissalData
		>
{
	extract(
		response: AppRecMutationResponse | undefined,
	): UndoDismissalResponse['undoDismissal'] | null {
		return this.isUndoDismissalResponse(response) ? response.undoDismissal : null;
	}

	getSuccessResultCreator(
		response: Response,
		undoDismissalResponse: UndoDismissalResponse['undoDismissal'],
	): UndoDismissalSuccessResultCreator {
		return new UndoDismissalSuccessResultCreator(
			response,
			this.getUndoDismissalResult(undoDismissalResponse),
		);
	}

	private getUndoDismissalResult(
		undoDismissalResponse: UndoDismissalResponse['undoDismissal'],
	): NonNullable<UndoDismissalResponse['undoDismissal']['result']> {
		if (!undoDismissalResponse.result) {
			throw new Error('Undo dismissal result is missing');
		}

		return undoDismissalResponse.result;
	}

	private isUndoDismissalResponse(
		response: AppRecMutationResponse | undefined,
	): response is UndoDismissalResponse {
		return response?.hasOwnProperty('undoDismissal') ?? false;
	}
}
